/**
 * Iron Therapy state routing — main page + NYC/national Ads LP only.
 *
 * Problem: the Servicify Mobile Service intake has a 14-state dropdown
 * (Servicify field 24193), but the State choice was saved as a cart line
 * property only. The cart kept the page's own NY mobile variant, which lives
 * in the NY-only $0 delivery profile — so any non-NY address failed checkout
 * with "not available for delivery".
 *
 * This module intercepts /cart/add(.js) on the two NY entry pages and swaps
 * the NY mobile variant for the selected state's own mobile variant (same
 * package: Single <-> Single, Buy 5 Get 1 Free <-> Buy 5 Get 1 Free).
 * Every state target lives in its own $0 "Mobile visit included" profile,
 * so pricing ($699 / $3,495) and $0 nurse fee are preserved and no separate
 * $200 Iron fee can appear. Non-iron profiles are untouched.
 *
 * Fail-open: any unknown state, missing target, or parse error leaves the
 * request body untouched (previous behavior).
 *
 * Scoped by layout/theme.liquid to product handles:
 *   'iron-therapy' and 'lp-iron-therapy' (exact match only).
 */
(function () {
  'use strict';

  var MAIN_SINGLE = 40881262723145;
  var MAIN_BUY5 = 41867145248841;
  var LP_NY_SINGLE = 67605717680201;
  var LP_NY_BUY5 = 67605717712969;

  // State -> { single, buy5 } canonical iron mobile variants ($699 / $3,495).
  // LP duplicates for TX/NJ/GA/FL resolved per entry page below.
  var STATE_VARIANTS = {
    'New York': { single: 40881262723145, buy5: 41867145248841 },
    'New Jersey': { single: 55636655013961, buy5: 55904809615433 },
    'Maryland': { single: 56103279263817, buy5: 56103279296585 },
    'Virginia': { single: 56090292813897, buy5: 56090292846665 },
    'Pennsylvania': { single: 56104289632329, buy5: 56104289665097 },
    'Ohio': { single: 56107581243465, buy5: 56107581276233 },
    'Georgia': { single: 56088396726345, buy5: 56088396759113 },
    'Missouri': { single: 56111138078793, buy5: 56111138111561 },
    'North Carolina': { single: 56111231795273, buy5: 56111231828041 },
    'Florida': { single: 56083616825417, buy5: 56083616858185 },
    'Arizona': { single: 67644237545545, buy5: 67644237578313 },
    'Texas': { single: 55901057351753, buy5: 55901057384521 },
    'California': { single: 55954982928457, buy5: 55954982961225 },
    'Colorado': { single: 56123428765769, buy5: 56123428798537 },
    'Washington': { single: 56110191837257, buy5: 56110191870025 }
  };

  // Same-state LP duplicates (NYC Ads LP prefers these to stay in-LP-family).
  var STATE_LP_VARIANTS = {
    'New York': { single: 67605717680201, buy5: 67605717712969 },
    'Texas': { single: 67605717778505, buy5: 67605717811273 },
    'New Jersey': { single: 67605717909577, buy5: 67605717942345 },
    'Georgia': { single: 67605718040649, buy5: 67605718073417 },
    'Florida': { single: 67605718138953, buy5: 67605718171721 }
  };

  function entryPageKind() {
    var m = (window.location.pathname || '').match(/^\/products\/(iron-therapy|lp-iron-therapy)\/?$/);
    if (!m) return null;
    return m[1] === 'iron-therapy' ? 'main' : 'lp';
  }

  function normalizeState(s) {
    var t = String(s == null ? '' : s).trim().toLowerCase();
    for (var name in STATE_VARIANTS) {
      if (name.toLowerCase() === t) return name;
    }
    return null;
  }

  function stateFromProperties(props) {
    if (!props) return null;
    // Servicify posts the intake as line properties (key seen live: _State__24193).
    for (var k in props) {
      if (!Object.prototype.hasOwnProperty.call(props, k)) continue;
      if (/state/i.test(k)) {
        var hit = normalizeState(props[k]);
        if (hit) return hit;
      }
    }
    for (var k2 in props) {
      if (!Object.prototype.hasOwnProperty.call(props, k2)) continue;
      var v = props[k2];
      if (typeof v === 'string') {
        var hit2 = normalizeState(v);
        if (hit2) return hit2;
      }
    }
    return null;
  }

  function isSingleTrigger(id) {
    return id === MAIN_SINGLE || id === LP_NY_SINGLE;
  }

  function isBuy5Trigger(id) {
    return id === MAIN_BUY5 || id === LP_NY_BUY5;
  }

  function resolveTarget(state, cartVariantId, kind) {
    if (!state || state === 'New York') return null;
    var single = isSingleTrigger(cartVariantId);
    var buy5 = isBuy5Trigger(cartVariantId);
    if (!single && !buy5) return null;
    // NYC Ads LP prefers same-state LP duplicates where they exist.
    if (kind === 'lp' && STATE_LP_VARIANTS[state]) {
      return single ? STATE_LP_VARIANTS[state].single : STATE_LP_VARIANTS[state].buy5;
    }
    var t = STATE_VARIANTS[state];
    if (!t) return null;
    return single ? t.single : t.buy5;
  }

  function remapBody(body, kind) {
    if (body == null) return body;
    var isString = typeof body === 'string';
    var data;
    try {
      data = isString ? JSON.parse(body) : body;
    } catch (e) {
      return body;
    }
    var items = Array.isArray(data.items) ? data.items : [data];
    var changed = false;
    items.forEach(function (it) {
      if (!it || it.id == null) return;
      var id = Number(it.id);
      var state = stateFromProperties(it.properties);
      var target = resolveTarget(state, id, kind);
      if (target && target !== id) {
        it.id = target;
        changed = true;
      }
    });
    if (!changed) return body;
    if (isString) {
      try {
        return JSON.stringify(data);
      } catch (e) {
        return body;
      }
    }
    return data;
  }

  function isCartAdd(url) {
    return typeof url === 'string' && /\/cart\/add(\.js)?(\?|$)/.test(url);
  }

  function install(kind) {
    if (window.__dgIronStateRouting) return;
    window.__dgIronStateRouting = true;

    if (window.fetch) {
      var origFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url);
        if (isCartAdd(url) && init && init.body != null) {
          try {
            init = Object.assign({}, init, { body: remapBody(init.body, kind) });
          } catch (e) { /* fail open */ }
        }
        return origFetch.call(this, input, init);
      };
    }

    if (window.XMLHttpRequest) {
      var origOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        this.__dgStateUrl = url;
        return origOpen.apply(this, arguments);
      };
      var origSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.send = function (body) {
        try {
          if (isCartAdd(this.__dgStateUrl) && body != null) body = remapBody(body, kind);
        } catch (e) { /* fail open */ }
        return origSend.call(this, body);
      };
    }
  }

  // Test hook (also used by node unit test via file eval).
  window.__dgIronStateRoutingTest = {
    normalizeState: normalizeState,
    stateFromProperties: stateFromProperties,
    resolveTarget: resolveTarget,
    remapBody: remapBody
  };

  var kind = entryPageKind();
  if (kind) install(kind);
})();
